import React, { useState } from "react";

import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  MenuItem,
  Alert,
  Divider,
  IconButton,
} from "@mui/material";

import {
  Add,
  CalendarMonthOutlined,
  LockOutlined,
  SaveOutlined,
  Clear,
  EditOutlined,
} from "@mui/icons-material";

const FinancialYear = () => {
  // ============================================================
  // STATE
  // ============================================================

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [financialYears, setFinancialYears] = useState([
    {
      id: 1,
      name: "FY 2026-27",
      startDate: "2026-04-01",
      endDate: "2027-03-31",
      status: "Active",
      current: true,
    },
    {
      id: 2,
      name: "FY 2025-26",
      startDate: "2025-04-01",
      endDate: "2026-03-31",
      status: "Closed",
      current: false,
    },
    {
      id: 3,
      name: "FY 2024-25",
      startDate: "2024-04-01",
      endDate: "2025-03-31",
      status: "Closed",
      current: false,
    },
  ]);

  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    status: "Active",
  });

  // ============================================================
  // FORM CHANGE
  // ============================================================

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // RESET FORM
  // ============================================================

  const resetForm = () => {
    setFormData({
      name: "",
      startDate: "",
      endDate: "",
      status: "Active",
    });

    setEditId(null);
  };

  // ============================================================
  // ADD
  // ============================================================

  const handleAdd = () => {
    resetForm();

    setShowForm(true);

    setTimeout(() => {
      document
        .getElementById("financial-year-form")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }, 100);
  };

  // ============================================================
  // EDIT
  // ============================================================

  const handleEdit = (item) => {
    setEditId(item.id);

    setFormData({
      name: item.name,
      startDate: item.startDate,
      endDate: item.endDate,
      status: item.status,
    });

    setShowForm(true);

    setTimeout(() => {
      document
        .getElementById("financial-year-form")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }, 100);
  };

  // ============================================================
  // CLOSE FORM
  // ============================================================

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  // ============================================================
  // SAVE
  // ============================================================

  const handleSave = () => {
    if (
      !formData.name.trim() ||
      !formData.startDate ||
      !formData.endDate
    ) {
      alert("Please enter all required fields.");
      return;
    }

    if (formData.endDate <= formData.startDate) {
      alert("End date must be greater than start date.");
      return;
    }

    // EDIT
    if (editId) {
      setFinancialYears((prev) =>
        prev.map((item) =>
          item.id === editId
            ? {
                ...item,
                name: formData.name,
                startDate: formData.startDate,
                endDate: formData.endDate,
                status: formData.status,
              }
            : item
        )
      );
    }

    // ADD
    else {
      const newYear = {
        id: Date.now(),
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
        current: financialYears.length === 0,
      };

      setFinancialYears((prev) => [
        newYear,
        ...prev,
      ]);
    }

    handleCloseForm();
  };

  // ============================================================
  // DELETE
  // ============================================================

  const handleDelete = (id) => {
    const selected = financialYears.find(
      (item) => item.id === id
    );

    if (!selected) {
      return;
    }

    if (selected.current) {
      alert(
        "The current financial year cannot be deleted."
      );
      return;
    }

    if (selected.status === "Closed") {
      alert(
        "A closed financial year cannot be deleted."
      );
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selected.name}?`
    );

    if (!confirmed) {
      return;
    }

    setFinancialYears((prev) =>
      prev.filter((item) => item.id !== id)
    );
  };

  // ============================================================
  // SET CURRENT
  // ============================================================

  const handleSetCurrent = (id) => {
    const selected = financialYears.find(
      (item) => item.id === id
    );

    if (!selected) {
      return;
    }

    if (selected.status === "Closed") {
      alert(
        "A closed financial year cannot be made current."
      );
      return;
    }

    const confirmed = window.confirm(
      `Set ${selected.name} as the current financial year?`
    );

    if (!confirmed) {
      return;
    }

    setFinancialYears((prev) =>
      prev.map((item) => ({
        ...item,
        current: item.id === id,
        status:
          item.id === id
            ? "Active"
            : item.status,
      }))
    );
  };

  // ============================================================
  // CLOSE FINANCIAL YEAR
  // ============================================================

  const handleCloseYear = (id) => {
    const selected = financialYears.find(
      (item) => item.id === id
    );

    if (!selected) {
      return;
    }

    if (selected.current) {
      alert(
        "You cannot close the current financial year. Please set another financial year as current first."
      );
      return;
    }

    if (selected.status === "Closed") {
      return;
    }

    const confirmed = window.confirm(
      `Close ${selected.name}?`
    );

    if (!confirmed) {
      return;
    }

    setFinancialYears((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status: "Closed",
              current: false,
            }
          : item
      )
    );
  };

  // ============================================================
  // FORMAT DATE
  // ============================================================

  const formatDate = (date) => {
    if (!date) {
      return "";
    }

    const parts = date.split("-");

    if (parts.length !== 3) {
      return date;
    }

    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // ============================================================
  // CURRENT YEAR
  // ============================================================

  const currentYear = financialYears.find(
    (item) => item.current
  );

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: "#F5F7F9",
        pb: 6,
      }}
    >
      {/* ======================================================
          PAGE HEADER
      ====================================================== */}

      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E4E7EC",
          px: {
            xs: 2,
            sm: 2.5,
            md: 3,
          },
          py: 2.2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: {
                  xs: 21,
                  md: 24,
                },
                fontWeight: 700,
                color: "#172B4D",
                lineHeight: 1.2,
              }}
            >
              Financial Year
            </Typography>

            <Typography
              sx={{
                mt: 0.6,
                fontSize: 13,
                color: "#667085",
              }}
            >
              Manage your company's accounting
              periods and financial years.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              height: 40,
              px: 2.4,
              backgroundColor: "#008B8B",
              borderRadius: "6px",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              boxShadow: "none",
              whiteSpace: "nowrap",

              "&:hover": {
                backgroundColor: "#006F6F",
                boxShadow: "none",
              },
            }}
          >
            Add Financial Year
          </Button>
        </Box>
      </Box>

      {/* ======================================================
          PAGE CONTENT
      ====================================================== */}

      <Box
        sx={{
          px: {
            xs: 1.5,
            sm: 2,
            md: 3,
          },
          pt: 2,
        }}
      >
        {/* ====================================================
            CURRENT FINANCIAL YEAR
        ==================================================== */}

        {currentYear && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: "1px solid #E4E7EC",
              borderRadius: "6px",
              backgroundColor: "#FFFFFF",
              overflow: "hidden",
            }}
          >
            {/* CARD HEADER */}

            <Box
              sx={{
                px: {
                  xs: 1.8,
                  md: 2.2,
                },
                py: 1.6,
                borderBottom:
                  "1px solid #EAECF0",
                backgroundColor: "#FCFCFD",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#172B4D",
                }}
              >
                Current Financial Year
              </Typography>

              <Typography
                sx={{
                  mt: 0.3,
                  fontSize: 11.5,
                  color: "#98A2B3",
                }}
              >
                The financial year currently used by
                the CRM
              </Typography>
            </Box>

            {/* CARD CONTENT */}

            <Box
              sx={{
                px: {
                  xs: 1.8,
                  md: 2.2,
                },
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#E6F7F7",
                      color: "#008B8B",
                      flexShrink: 0,
                    }}
                  >
                    <CalendarMonthOutlined />
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#172B4D",
                        }}
                      >
                        {currentYear.name}
                      </Typography>

                      <Chip
                        label="CURRENT"
                        size="small"
                        sx={{
                          height: 22,
                          borderRadius: "6px",
                          backgroundColor:
                            "#E6F7F7",
                          color: "#007878",
                          fontSize: 9.5,
                          fontWeight: 700,
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        mt: 0.4,
                        fontSize: 12,
                        color: "#667085",
                      }}
                    >
                      {formatDate(
                        currentYear.startDate
                      )}{" "}
                      to{" "}
                      {formatDate(
                        currentYear.endDate
                      )}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "6px",
                    backgroundColor: "#ECFDF3",
                    color: "#027A48",
                    fontSize: 11.5,
                    fontWeight: 600,
                  }}
                >
                  Active Financial Year
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* ====================================================
            INFORMATION
        ==================================================== */}

        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: "6px",
            backgroundColor: "#F5FAFF",
            border: "1px solid #D6EBFF",
            color: "#344054",
            fontSize: 12.5,

            "& .MuiAlert-icon": {
              color: "#1976D2",
            },
          }}
        >
          The current financial year will be used for
          sales, purchases, invoices, receipts,
          payments and financial reports.
        </Alert>

        {/* ====================================================
            FINANCIAL YEARS TABLE
        ==================================================== */}

        <Paper
          elevation={0}
          sx={{
            mb: 2,
            border: "1px solid #E4E7EC",
            borderRadius: "6px",
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          {/* TABLE HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.8,
                md: 2.2,
              },
              py: 1.6,

              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              gap: 2,

              borderBottom:
                "1px solid #EAECF0",
              backgroundColor: "#FCFCFD",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#172B4D",
                }}
              >
                Financial Years
              </Typography>

              <Typography
                sx={{
                  mt: 0.3,
                  fontSize: 11.5,
                  color: "#98A2B3",
                }}
              >
                {financialYears.length} financial
                years configured
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={handleAdd}
              sx={{
                height: 34,
                borderRadius: "6px",
                textTransform: "none",
                borderColor: "#008B8B",
                color: "#008B8B",
                fontSize: 12,
                fontWeight: 600,

                "&:hover": {
                  borderColor: "#006F6F",
                  backgroundColor: "#F0FAFA",
                },
              }}
            >
              Add Year
            </Button>
          </Box>

          {/* ==================================================
              DESKTOP TABLE
          ================================================== */}

          <Box
            sx={{
              display: {
                xs: "none",
                md: "block",
              },
              p: 2,
            }}
          >
            <Box
              sx={{
                border:
                  "1px solid #EAECF0",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              {/* HEADER */}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "1.6fr 1fr 1fr 0.9fr 1.6fr",
                  alignItems: "center",
                  px: 1.8,
                  py: 1.25,
                  backgroundColor: "#F8FAFC",
                  borderBottom:
                    "1px solid #EAECF0",
                }}
              >
                <TableHeader>
                  Financial Year
                </TableHeader>

                <TableHeader>
                  Start Date
                </TableHeader>

                <TableHeader>
                  End Date
                </TableHeader>

                <TableHeader>
                  Status
                </TableHeader>

                <TableHeader align="right">
                  Actions
                </TableHeader>
              </Box>

              {/* ROWS */}

              {financialYears.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "1.6fr 1fr 1fr 0.9fr 1.6fr",
                    alignItems: "center",
                    px: 1.8,
                    py: 1.4,
                    minHeight: 66,
                    borderBottom:
                      "1px solid #F0F2F5",

                    "&:last-child": {
                      borderBottom: "none",
                    },

                    "&:hover": {
                      backgroundColor:
                        "#FAFCFC",
                    },
                  }}
                >
                  {/* FINANCIAL YEAR */}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        backgroundColor:
                          item.current
                            ? "#E6F7F7"
                            : "#F2F4F7",
                        color: item.current
                          ? "#008B8B"
                          : "#667085",
                      }}
                    >
                      <CalendarMonthOutlined
                        fontSize="small"
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "#344054",
                        }}
                      >
                        {item.name}
                      </Typography>

                      {item.current && (
                        <Typography
                          sx={{
                            mt: 0.2,
                            fontSize: 10,
                            color: "#008B8B",
                            fontWeight: 600,
                          }}
                        >
                          Current Financial Year
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* START DATE */}

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#475467",
                    }}
                  >
                    {formatDate(
                      item.startDate
                    )}
                  </Typography>

                  {/* END DATE */}

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#475467",
                    }}
                  >
                    {formatDate(
                      item.endDate
                    )}
                  </Typography>

                  {/* STATUS */}

                  <Box>
                    {item.status === "Active" ? (
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          height: 24,
                          borderRadius: "6px",
                          backgroundColor:
                            "#ECFDF3",
                          color: "#027A48",
                          fontWeight: 600,
                          fontSize: 10.5,
                        }}
                      />
                    ) : (
                      <Chip
                        label="Closed"
                        size="small"
                        icon={
                          <LockOutlined
                            sx={{
                              fontSize:
                                "15px !important",
                            }}
                          />
                        }
                        sx={{
                          height: 24,
                          borderRadius: "6px",
                          backgroundColor:
                            "#F2F4F7",
                          color: "#667085",
                          fontWeight: 600,
                          fontSize: 10.5,
                        }}
                      />
                    )}
                  </Box>

                  {/* ACTIONS */}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent:
                        "flex-end",
                      alignItems: "center",
                      gap: 0.7,
                      flexWrap: "wrap",
                    }}
                  >
                    {!item.current &&
                      item.status !== "Closed" && (
                        <ActionButton
                          color="#008B8B"
                          background="#EAF7F7"
                          onClick={() =>
                            handleSetCurrent(
                              item.id
                            )
                          }
                        >
                          Set Current
                        </ActionButton>
                      )}

                    <ActionButton
                      color="#175CD3"
                      background="#EFF8FF"
                      onClick={() =>
                        handleEdit(item)
                      }
                    >
                      <EditOutlined
                        sx={{
                          fontSize: 14,
                          mr: 0.4,
                        }}
                      />
                      Edit
                    </ActionButton>

                    {!item.current &&
                      item.status !== "Closed" && (
                        <>
                          <ActionButton
                            color="#B54708"
                            background="#FFFAEB"
                            onClick={() =>
                              handleCloseYear(
                                item.id
                              )
                            }
                          >
                            Close
                          </ActionButton>

                          <ActionButton
                            color="#B42318"
                            background="#FEF3F2"
                            onClick={() =>
                              handleDelete(
                                item.id
                              )
                            }
                          >
                            Delete
                          </ActionButton>
                        </>
                      )}
                  </Box>
                </Box>
              ))}

              {/* EMPTY */}

              {financialYears.length === 0 && (
                <EmptyState
                  onAdd={handleAdd}
                />
              )}
            </Box>
          </Box>

          {/* ==================================================
              MOBILE
          ================================================== */}

          <Box
            sx={{
              display: {
                xs: "block",
                md: "none",
              },
              p: 1.5,
            }}
          >
            {financialYears.map((item) => (
              <Box
                key={item.id}
                sx={{
                  p: 1.5,
                  mb: 1.2,
                  border:
                    "1px solid #EAECF0",
                  borderRadius: "6px",

                  "&:last-child": {
                    mb: 0,
                  },
                }}
              >
                {/* TOP */}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        backgroundColor:
                          item.current
                            ? "#E6F7F7"
                            : "#F2F4F7",
                        color: item.current
                          ? "#008B8B"
                          : "#667085",
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          "center",
                        flexShrink: 0,
                      }}
                    >
                      <CalendarMonthOutlined
                        fontSize="small"
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#344054",
                        }}
                      >
                        {item.name}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.3,
                          fontSize: 11,
                          color: "#667085",
                        }}
                      >
                        {formatDate(
                          item.startDate
                        )}{" "}
                        -{" "}
                        {formatDate(
                          item.endDate
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  {item.status === "Active" ? (
                    <Chip
                      label="Active"
                      size="small"
                      sx={{
                        height: 23,
                        borderRadius: "6px",
                        backgroundColor:
                          "#ECFDF3",
                        color: "#027A48",
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    />
                  ) : (
                    <Chip
                      label="Closed"
                      size="small"
                      sx={{
                        height: 23,
                        borderRadius: "6px",
                        backgroundColor:
                          "#F2F4F7",
                        color: "#667085",
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 1.4 }} />

                {/* ACTIONS */}

                <Box
                  sx={{
                    display: "flex",
                    gap: 0.7,
                    flexWrap: "wrap",
                  }}
                >
                  {!item.current &&
                    item.status !== "Closed" && (
                      <ActionButton
                        color="#008B8B"
                        background="#EAF7F7"
                        onClick={() =>
                          handleSetCurrent(
                            item.id
                          )
                        }
                      >
                        Set Current
                      </ActionButton>
                    )}

                  <ActionButton
                    color="#175CD3"
                    background="#EFF8FF"
                    onClick={() =>
                      handleEdit(item)
                    }
                  >
                    <EditOutlined
                      sx={{
                        fontSize: 14,
                        mr: 0.4,
                      }}
                    />
                    Edit
                  </ActionButton>

                  {!item.current &&
                    item.status !== "Closed" && (
                      <>
                        <ActionButton
                          color="#B54708"
                          background="#FFFAEB"
                          onClick={() =>
                            handleCloseYear(
                              item.id
                            )
                          }
                        >
                          Close
                        </ActionButton>

                        <ActionButton
                          color="#B42318"
                          background="#FEF3F2"
                          onClick={() =>
                            handleDelete(
                              item.id
                            )
                          }
                        >
                          Delete
                        </ActionButton>
                      </>
                    )}
                </Box>
              </Box>
            ))}

            {financialYears.length === 0 && (
              <EmptyState
                onAdd={handleAdd}
              />
            )}
          </Box>
        </Paper>

        {/* ====================================================
            ADD / EDIT FORM
        ==================================================== */}

        {showForm && (
          <Paper
            id="financial-year-form"
            elevation={0}
            sx={{
              border:
                "1px solid #E4E7EC",
              borderRadius: "6px",
              backgroundColor: "#FFFFFF",
              overflow: "hidden",
            }}
          >
            {/* FORM HEADER */}

            <Box
              sx={{
                px: {
                  xs: 1.8,
                  md: 2.2,
                },
                py: 1.6,

                display: "flex",
                justifyContent:
                  "space-between",
                alignItems: "center",

                borderBottom:
                  "1px solid #EAECF0",
                backgroundColor: "#FCFCFD",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#172B4D",
                  }}
                >
                  {editId
                    ? "Edit Financial Year"
                    : "Add Financial Year"}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.3,
                    fontSize: 11.5,
                    color: "#98A2B3",
                  }}
                >
                  Configure the financial year
                  accounting period.
                </Typography>
              </Box>

              <IconButton
                size="small"
                onClick={handleCloseForm}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "6px",
                  color: "#667085",

                  "&:hover": {
                    backgroundColor:
                      "#F2F4F7",
                  },
                }}
              >
                <Clear fontSize="small" />
              </IconButton>
            </Box>

            {/* FORM */}

            <Box
              sx={{
                p: {
                  xs: 1.8,
                  md: 2.2,
                },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {/* NAME */}

                <Box
                  sx={{
                    gridColumn: {
                      xs: "auto",
                      md: "span 2",
                    },
                  }}
                >
                  <Field
                    required
                    label="Financial Year Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="FY 2027-28"
                  />
                </Box>

                {/* START */}

                <Field
                  required
                  type="date"
                  label="Start Date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />

                {/* END */}

                <Field
                  required
                  type="date"
                  label="End Date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                />

                {/* STATUS */}

                <Box
                  sx={{
                    gridColumn: {
                      xs: "auto",
                      md: "span 1",
                    },
                  }}
                >
                  <Field
                    select
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <MenuItem value="Active">
                      Active
                    </MenuItem>

                    <MenuItem value="Closed">
                      Closed
                    </MenuItem>
                  </Field>
                </Box>
              </Box>

              {/* FORM FOOTER */}

              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop:
                    "1px solid #EAECF0",

                  display: "flex",
                  justifyContent:
                    "flex-end",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseForm}
                  sx={{
                    height: 38,
                    px: 2,
                    borderRadius: "6px",
                    textTransform: "none",
                    borderColor: "#D0D5DD",
                    color: "#475467",
                    fontSize: 12.5,

                    "&:hover": {
                      borderColor:
                        "#98A2B3",
                      backgroundColor:
                        "#F9FAFB",
                    },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  startIcon={<SaveOutlined />}
                  onClick={handleSave}
                  sx={{
                    height: 38,
                    px: 2.5,
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12.5,
                    backgroundColor: "#008B8B",
                    boxShadow: "none",

                    "&:hover": {
                      backgroundColor:
                        "#006F6F",
                      boxShadow: "none",
                    },
                  }}
                >
                  {editId
                    ? "Update Financial Year"
                    : "Create Financial Year"}
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

// ================================================================
// TABLE HEADER
// ================================================================

const TableHeader = ({
  children,
  align = "left",
}) => {
  return (
    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        color: "#667085",
        textTransform: "uppercase",
        textAlign: align,
        letterSpacing: "0.02em",
      }}
    >
      {children}
    </Typography>
  );
};

// ================================================================
// ACTION BUTTON
// ================================================================

const ActionButton = ({
  children,
  color,
  background,
  onClick,
}) => {
  return (
    <Button
      size="small"
      onClick={onClick}
      sx={{
        minWidth: 0,
        minHeight: 30,
        px: 1.1,
        borderRadius: "6px",
        textTransform: "none",
        fontSize: 11,
        fontWeight: 600,
        color,
        backgroundColor: background,
        display: "inline-flex",
        alignItems: "center",

        "&:hover": {
          backgroundColor: background,
          filter: "brightness(0.97)",
        },
      }}
    >
      {children}
    </Button>
  );
};

// ================================================================
// FIELD
// ================================================================

const Field = ({
  label,
  name,
  value,
  onChange,
  required = false,
  select = false,
  children,
  type = "text",
  placeholder,
}) => {
  return (
    <TextField
      fullWidth
      size="small"
      required={required}
      select={select}
      type={type}
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      InputLabelProps={
        type === "date"
          ? {
              shrink: true,
            }
          : undefined
      }
      sx={{
        "& .MuiOutlinedInput-root": {
          minHeight: 40,
          borderRadius: "6px",
          backgroundColor: "#FFFFFF",
          fontSize: 13,

          "& fieldset": {
            borderColor: "#D0D5DD",
            borderWidth: "1px",
          },

          "&:hover fieldset": {
            borderColor: "#98A2B3",
          },

          "&.Mui-focused fieldset": {
            borderColor: "#008B8B",
            borderWidth: "1px",
          },
        },

        "& .MuiInputLabel-root": {
          fontSize: 12,
          color: "#667085",
        },

        "& .MuiInputLabel-root.Mui-focused": {
          color: "#008B8B",
        },

        "& .MuiInputBase-input": {
          boxSizing: "border-box",
          padding: "9px 11px",
        },

        "& .MuiSelect-select": {
          minHeight:
            "40px !important",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          padding:
            "9px 32px 9px 11px !important",
        },
      }}
    >
      {children}
    </TextField>
  );
};

// ================================================================
// EMPTY STATE
// ================================================================

const EmptyState = ({ onAdd }) => {
  return (
    <Box
      sx={{
        py: 6,
        textAlign: "center",
      }}
    >
      <CalendarMonthOutlined
        sx={{
          fontSize: 42,
          color: "#CBD5E1",
          mb: 1,
        }}
      />

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "#475467",
        }}
      >
        No financial years found
      </Typography>

      <Typography
        sx={{
          fontSize: 12,
          color: "#98A2B3",
          mt: 0.5,
          mb: 2,
        }}
      >
        Create your first financial year.
      </Typography>

      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={onAdd}
        sx={{
          backgroundColor: "#008B8B",
          borderRadius: "6px",
          textTransform: "none",
          boxShadow: "none",

          "&:hover": {
            backgroundColor: "#006F6F",
            boxShadow: "none",
          },
        }}
      >
        Add Financial Year
      </Button>
    </Box>
  );
};

export default FinancialYear;